import { getProductDisplayName } from '../components/productConfig';
import type { UiProductType } from '../types';

/**
 * Priority order for displaying impacted products.
 * Key products like Jira and Confluence are shown first.
 * Products not in this list will appear at the end in their original order.
 */
const PRODUCT_PRIORITY_ORDER: UiProductType[] = [
  'JIRA_SOFTWARE',
  'JIRA_CORE',
  'CONFLUENCE',
  'CLOUD_ADMIN',
  'ROVO',
  'JIRA_SERVICE_MANAGEMENT',
  'JIRA_WORK_MANAGEMENT',
  'JIRA_PRODUCT_DISCOVERY',
  'LOOM',
  'TRELLO',
  'BITBUCKET',
  'COMPASS',
  'OPSGENIE',
  'STATUSPAGE',
  'JIRA_ALIGN',
  'JIRA_ADMIN',
  'JIRA_CUSTOMER_SERVICE',
  'JIRA_INCIDENT_MANAGER',
];

/**
 * Normalizes a product type to uppercase for comparison.
 * Handles both uppercase (JIRA_SOFTWARE) and lowercase (jira_software) inputs.
 */
const normalizeProductType = (product: UiProductType): string =>
  product.toUpperCase();

/**
 * Sorts products by priority order so key products (e.g. Jira, Confluence)
 * are shown first in the UI. Products not in the priority list are placed at the end.
 * Deduplicates products that share the same display name (e.g. JIRA_SOFTWARE and
 * JIRA_CORE both display as "Jira" — only the higher-priority one is kept).
 *
 * Follows the same pattern as the admin hub's sortImpactedApps utility.
 */
export const sortProducts = (
  products: readonly UiProductType[],
): UiProductType[] => {
  if (products.length === 0) {
    return [];
  }

  const productsCopy = [...products];

  const normalizedPriorityOrder =
    PRODUCT_PRIORITY_ORDER.map(normalizeProductType);

  const sorted = productsCopy.sort((a, b) => {
    const weightA = normalizedPriorityOrder.indexOf(normalizeProductType(a));
    const weightB = normalizedPriorityOrder.indexOf(normalizeProductType(b));

    return (
      (weightA === -1 ? Infinity : weightA) -
      (weightB === -1 ? Infinity : weightB)
    );
  });

  // Deduplicate by display name — keep the first (highest priority) occurrence
  const seenDisplayNames = new Set<string>();
  return sorted.filter((product) => {
    const displayName = getProductDisplayName(product);
    if (seenDisplayNames.has(displayName)) {
      return false;
    }
    seenDisplayNames.add(displayName);
    return true;
  });
};
